import { createFeatureSelector, createSelector } from '@ngrx/store';

import sortBy from 'lodash/sortBy';
import flatMap from 'lodash/flatMap';
import groupBy from 'lodash/groupBy';
import flatten from 'lodash/flatten';

import { PATIENT_FEATURE_KEY, PatientState } from './patient.reducer';

import {
  groupByVariation,
  refBenefitsValidation,
  PlanLevelAccumulation,
  Network,
  Specialty,
  STATIC_BENEFIT_TAGS,
} from '@cigna/chcp/patient/shared/util';

import { claimSystemCodes } from '@cigna/chcp/shared/util';

const getPatientState =
  createFeatureSelector<PatientState>(PATIENT_FEATURE_KEY);
const displayPatientDetails = createSelector(
  getPatientState,
  (state) => state.displayPatientDetails,
);
const estimateType = createSelector(
  getPatientState,
  (state) => state.estimateType,
);

const getCostEstimationResult = createSelector(
  getPatientState,
  (state) => state.estimateCostResult,
);
const submitEstimateError = createSelector(
  getPatientState,
  (state) => state.submitEstimateError,
);
const getAmInNetworkStatus = createSelector(
  getPatientState,
  (state) => state.amInNetworkStatus,
);

const getPageStage = createSelector(
  getPatientState,
  (state) => state.pageStage,
);

const getError = createSelector(getPatientState, (state) => state.error);
const getSearchRows = createSelector(
  getPatientState,
  (state) => state.searches,
);

const getResults = createSelector(getPatientState, (state) => state.results);

const getProductDetails = createSelector(
  getPatientState,
  (state) => state.productDetails,
);

const getFrequencyDetails = createSelector(
  getPatientState,
  (state) => state.frequencyAgePlanDetails,
);

const getResultsFiltered = createSelector(getPatientState, (state) =>
  state.results.filter((result) =>
    state.resultFilter === 'all'
      ? state.results
      : result.display.coverageStatus === state.resultFilter,
  ),
);

const getResultFilter = createSelector(
  getPatientState,
  (state) => state.resultFilter,
);

const getResultFilterOptions = createSelector(getPatientState, (state) =>
  state.results
    .map((result) => result.display.coverageStatus)
    .filter((value, index, self) => self.indexOf(value) === index)
    .filter((status) => status !== ''),
);

const getVisionOnlyDetails = createSelector(
  getPatientState,
  (state) => state.results[0]?.detail,
);

const isMultipleMatch = createSelector(
  getPatientState,
  (state) => state.multipleMatched,
);

const isSingleNonExactMatch = createSelector(
  getPatientState,
  (state) => state.singleResponse && state.noneMatched,
);

const getPatientRowDetailLoading = createSelector(
  getPatientState,
  (state) => state.patientRowDetailLoading,
);

const getPatientEstimateDetailsLoading = createSelector(
  getPatientState,
  (state) => state.patientEstimateDetailsLoading,
);

const getPatientEstimateResultLoading = createSelector(
  getPatientState,
  (state) => state.patientEstimateResultLoading,
);

const getPatientRowInactiveLoading = createSelector(
  getPatientState,
  (state) => state.patientRowInactiveLoading,
);

const getPatientDetail = createSelector(
  getPatientState,
  (state) => state.patientDetail,
);

const getPatientAsOfDate = createSelector(getPatientDetail, (detail) =>
  detail ? detail.asOfDate : null,
);

const getBenefitReportDetails = createSelector(
  getPatientState,
  (state) => state.benefitReportDetails,
);

const getCcfBenefit = createSelector(
  getPatientState,
  (state) => state.ccfBenefit,
);
const getCoverageDetail = createSelector(
  getPatientState,
  (state) => state.coverageDetail,
);

const getCostEstimationDetail = createSelector(
  getPatientState,
  (state) => state.costEstimationDetail,
);

const idCardDetails = createSelector(
  getPatientState,
  (state) => state.idCardDetails,
);
const getIdCardError = createSelector(
  getPatientState,
  (state) => state.idCardError,
);

const dentalOfficeDetails = createSelector(
  getPatientState,
  (state) => state.dentalOfficeDetails,
);

const getServiceProcedures = createSelector(
  getPatientState,
  (state) => state.serviceProcedures,
);
const getCobDetails = createSelector(
  getPatientState,
  (state) => state.cobDetails,
);

const getNotificationDetails = createSelector(
  getPatientState,
  (state) => state.notificationDetail,
);

const getPharmacyBenefitDetails = createSelector(
  getPatientState,
  (state) => state.pharmacyBenefitDetails,
);

const getPatientCompositeKey = createSelector(
  getPatientState,
  (state) => state.patientCompositeKey,
);

const getAlliances = createSelector(
  getPatientState,
  (state) => state.alliances,
);

const getPatientRouteData = createSelector(
  getPatientCompositeKey,
  getPatientDetail,
  (compositeKey: string, res: any) => ({
    compositeKey,
    id: res ? res.details.id : '',
    name: res ? `${res.details.firstName} ${res.details.lastName}` : '',
  }),
);

const getPrimaryCareDetails = createSelector(
  getPatientState,
  (state) => state.primaryCareDetails,
);

const getSubscriberDetail = createSelector(
  getPatientState,
  (state) => state.subscriberDetail,
);

const getServiceContacts = createSelector(
  getPatientState,
  (state) => state.serviceContacts,
);

const getEligibilityDate = createSelector(
  getPatientState,
  (state) => state.eligibilityDate,
);

const getPlanLevelAccum = createSelector(
  getPatientState,
  (state) => state.planLevelAccumulation,
);

const serviceLevelAccum = createSelector(
  getPatientState,
  (state) => state.serviceLevelAccumulation,
);

const getReferenceData = createSelector(
  getPatientState,
  (state) => state.reference,
);

const getReferenceBenefits = createSelector(getReferenceData, (reference) =>
  reference && reference.benefits ? reference.benefits : null,
);

const getInfertilityServices = createSelector(
  getPatientState,
  (state) => state.infertilityServices,
);

const getMaternityBenefit = createSelector(
  getPatientState,
  (state) => state.maternityBenefitDetails,
);

const getVisionServices = createSelector(
  getPatientState,
  (state) => state.visionServices,
);

const getStaticBenefitTags = createSelector(
  getCoverageDetail,
  getInfertilityServices,
  getMaternityBenefit,
  getVisionServices,
  // TODO: resolve CHCP disables by bot
  // eslint-disable-next-line sonarjs/cognitive-complexity
  (coverage, infertility, maternity, visionServices) =>
    coverage
      ? coverage.coverageType === 'MHSR'
        ? coverage.hasPDP
          ? ['PRESCRIPTION COVERAGE']
          : []
        : !['DEN', 'DENT'].includes(coverage.coverageType)
        ? STATIC_BENEFIT_TAGS.filter(
            (tag) =>
              (tag === 'PRESCRIPTION COVERAGE' && coverage.hasPDP) ||
              (tag === 'VISION SERVICES' &&
                !!visionServices &&
                coverage.claimSystemCode !== claimSystemCodes.Facets) ||
              (tag === 'INFERTILITY SERVICES' && !!infertility) ||
              (tag === 'MATERNITY' && !!maternity?.hasMaternity),
          )
        : []
      : [],
);

const getBenefitTags = createSelector(
  serviceLevelAccum,
  getReferenceBenefits,
  getStaticBenefitTags,
  (serviceAccum, refBenefits, staticBenefitTags) =>
    serviceAccum
      ? sortBy(
          Array.from(
            new Set(
              flatMap(serviceAccum.services, (service) =>
                flatMap(service.variations, (variation) => variation.tags),
              )
                .filter((tag: string) => tag.includes('CONT_'))
                // tag replace should happen before concat as static benefits wont have the CONT_ prefix.
                .map((tag: string) => tag.replace('CONT_', '').toUpperCase())
                .concat(...staticBenefitTags)
                .filter((tag: string) =>
                  refBenefitsValidation(refBenefits, tag),
                ),
            ),
          ),
        )
      : [],
);

const updateBenefitStructure = (variation: any) => {
  const benefitKeys = Object.keys(variation.benefits);
  const values: any[] = [];

  benefitKeys.forEach((benefit: string) => {
    if (
      benefit in variation.benefits &&
      variation.benefits[benefit].accumulations &&
      variation.benefits[benefit].accumulations.length > 0
    ) {
      values.push(groupByVariation(variation.benefits[benefit].accumulations));
    }
  });
  return flatten(values);
};

const getServiceLevelAccum = createSelector(serviceLevelAccum, (serviceAccum) =>
  serviceAccum
    ? {
        ...serviceAccum,
        services: serviceAccum.services.map((service) => ({
          ...service,
          variations: service.variations.map((variation) => ({
            ...variation,
            benefits: updateBenefitStructure(variation),
          })),
        })),
      }
    : [],
);

const serviceAccumTagData = (serAccum: any, tag: string) =>
  flatMap(serAccum.services, (service: any) => service.variations).filter(
    (variation: any) =>
      variation.tags.find(
        (varTag: string) => varTag.replace('CONT_', '').toUpperCase() === tag,
      ),
  );

const getAccumulatedServiceData = createSelector(
  getServiceLevelAccum,
  getBenefitTags,
  (serAccum, tags) =>
    tags.reduce((obj: any, tag: string) => {
      const data = serviceAccumTagData(serAccum, tag);
      if (data && data.length) {
        obj[tag] = data;
      }
      return obj;
    }, {}),
);

const getHealthWellness = createSelector(
  getPatientState,
  (state) => state.healthWellness,
);

const getProviderGroups = createSelector(
  getPatientState,
  (state) => state.providerGroup,
);

const getProviders = createSelector(
  getPatientState,
  (state) => state.providers,
);

const isAccountRestricted = createSelector(
  getCoverageDetail,
  (coverage) => !!coverage?.isAccountRestricted,
);

const getUnsortedProviderDetails = createSelector(
  getPatientState,
  (state) => state.providerDetails,
);

const getMedAmInNetworkSelectedTin = createSelector(
  getPatientState,
  (state) => state.medAmInNetworkSelectedTin,
);

const getProviderDetails = createSelector(
  getUnsortedProviderDetails,
  (details) => ({
    ...details,
    data: {
      networks: details?.data?.networks
        ? groupBy(details.data.networks, 'typeCode')
        : [],
      networkKeys: details?.data?.networks
        ? Array.from(
            new Set(
              details.data.networks.map((network: Network) => network.typeCode),
            ),
          )
        : [],
      specialties: details?.data?.specialties
        ? details.data.specialties
            .slice()
            .sort((a: Specialty, b: Specialty) => a.name.localeCompare(b.name))
        : [],
    },
  }),
);

const getInOutNetworkDetails = createSelector(
  getPatientState,
  (state) => state.inOutNetworkDetails,
);

const sureFitDetails = createSelector(
  getPatientState,
  (state) => state.sureFitResponse,
);

const getPlanLevelTags = createSelector(
  getPatientState,
  getPlanLevelAccum,
  getReferenceBenefits,
  // TODO: resolve CHCP disables by bot
  // eslint-disable-next-line sonarjs/cognitive-complexity
  (state, planAccum, refBenefits) => {
    const tags: string[] = [];
    if (!state.enhanced) {
      if (refBenefitsValidation(refBenefits, 'PATIENT_PLAN')) {
        tags.push('PATIENT_PLAN');
      }
      if (
        state.coverageDetail &&
        state.coverageDetail.coverageType &&
        !['DEN', 'DENT'].includes(state.coverageDetail.coverageType)
      ) {
        const planAccumTypes = planAccum.map(
          (plan: PlanLevelAccumulation) => `PLAN_LEVEL_${plan.type}`,
        );
        if (planAccumTypes.length) {
          planAccumTypes.forEach((type: string) => {
            if (refBenefitsValidation(refBenefits, type)) {
              tags.push(type);
            }
          });
        }
        if (
          state.primaryCareDetails &&
          ((!state.primaryCareDetails.pcpStatus &&
            (state.primaryCareDetails?.primaryCarePhysician ||
              state.primaryCareDetails?.providerOrganization)) ||
            (state.primaryCareDetails.pcpStatus &&
              state.coverageDetail.coverageType !== 'MHSR')) &&
          refBenefitsValidation(refBenefits, 'PCP_DETAILS')
        ) {
          tags.push('PCP_DETAILS');
        }
        if (
          state.cobDetails?.length &&
          refBenefitsValidation(refBenefits, 'COB_DETAILS')
        ) {
          tags.push('COB_DETAILS');
        }
        if (
          (state.ccfBenefit?.ccfbenefitFundList?.length ||
            state.ccfBenefit?.ccfType) &&
          refBenefitsValidation(refBenefits, 'CCF_BENEFIT')
        ) {
          tags.push('CCF_BENEFIT');
        }
        if (
          state.notificationDetail?.notificationData?.length &&
          refBenefitsValidation(refBenefits, 'NRPR')
        ) {
          tags.push('NRPR');
        }
        if (
          (state.healthWellness?.clinicalExpansion ||
            state.healthWellness?.healthWellnessIndicator ||
            state.coverageDetail?.coverageType === 'MED') &&
          state.coverageDetail?.coverageType !== 'MHSR' &&
          refBenefitsValidation(refBenefits, 'HEALTH_WELLNESS')
        ) {
          tags.push('HEALTH_WELLNESS');
        }
      } else {
        if (
          refBenefitsValidation(refBenefits, 'PLAN_ACCUM') &&
          ((state.coverageDetail?.claimSystemCode !== claimSystemCodes.Facets &&
            state.coverageDetail?.plan.type &&
            ['Dental PPO', 'Dental EPO', 'Dental Indemnity'].includes(
              state.coverageDetail.plan.type,
            )) ||
            state.coverageDetail?.claimSystemCode === claimSystemCodes.Facets)
        ) {
          tags.push('PLAN_ACCUM');
        }

        if (
          state.coverageDetail?.claimSystemCode !==
            claimSystemCodes.GovernmentBusinessSegmentFacets &&
          state.coverageDetail?.claimSystemCode === claimSystemCodes.Facets &&
          state.cobDetails?.length &&
          refBenefitsValidation(refBenefits, 'COB_DETAILS')
        ) {
          tags.push('COB_DETAILS');
        }
        if (
          state.coverageDetail?.plan?.type === 'Dental HMO' &&
          state.benefitReportDetails &&
          refBenefitsValidation(refBenefits, 'BENEFIT_REPORT')
        ) {
          tags.push('BENEFIT_REPORT');
        }
        if (
          state.coverageDetail?.claimSystemCode === claimSystemCodes.Facets &&
          state.frequencyAgePlanDetails?.specificCoverage &&
          (refBenefitsValidation(refBenefits, 'FREQ_COVERAGE_BENEFITS') ||
            refBenefitsValidation(refBenefits, 'FREQ_TITLE'))
        ) {
          tags.push(
            state.frequencyAgePlanDetails.showBenefitPDF
              ? 'FREQ_COVERAGE_BENEFITS'
              : 'FREQ_TITLE',
          );
        }
        if (
          state.coverageDetail?.claimSystemCode !== claimSystemCodes.Facets &&
          state.coverageDetail?.plan?.type &&
          ['Dental PPO', 'Dental EPO', 'Dental Indemnity'].includes(
            state.coverageDetail.plan.type,
          ) &&
          state.coverageDetail.plan.progressiveRegressiveInd !== 'Y' &&
          (refBenefitsValidation(refBenefits, 'FREQ_COVERAGE_BENEFITS') ||
            refBenefitsValidation(refBenefits, 'FREQ_TITLE'))
        ) {
          tags.push(
            state.frequencyAgePlanDetails &&
              state.frequencyAgePlanDetails.showBenefitPDF
              ? 'FREQ_COVERAGE_BENEFITS'
              : 'FREQ_TITLE',
          );
        }
      }
    }
    return tags;
  },
);

export const ohpTin = createSelector(getPatientState, (state) => state.ohpTin);

export const getPrecertUrl = createSelector(getPatientState, (state) =>
  state && state.precertData
    ? `${state.precertData.url}?sessionkey=${state.precertData.sessionKey}`
    : null,
);

export const getSureFitUrl = createSelector(getPatientState, (state) => {
  let zipcode = '';
  if (state?.patientDetail?.details?.addresses?.length) {
    zipcode = state.patientDetail.details.addresses[0].zip;
  }
  const networkCode =
    state?.coverageDetail?.claimSystemCode !== claimSystemCodes.Facets
      ? state?.coverageDetail?.networkDetails?.networkId
      : state.networkCode;
  if (
    state.coverageDetail?.coverages &&
    state.notificationDetail?.routerUrl &&
    state.notificationDetail.routerUrlInput &&
    networkCode
  ) {
    return state.coverageDetail?.coverages.includes('PDP')
      ? `${state.notificationDetail.routerUrl}?searchLocation=${zipcode}&${state.notificationDetail.routerUrlInput}&pharmacyProductCode=RX${networkCode}&pharmacyNetworkCode=${networkCode}&keepAliveURL=${window.location.origin}/app/assets/images/isam-keepalive.gif`
      : `${state.notificationDetail.routerUrl}?searchLocation=${zipcode}&${state.notificationDetail.routerUrlInput}&keepAliveURL=${window.location.origin}/app/assets/images/isam-keepalive.gif`;
  }
});

export const getPharmacyBenefit = createSelector(getPatientState, (state) => {
  if (
    state.pharmacyBenefitDetails &&
    state.pharmacyBenefitDetails.pharmacyBenefits &&
    state.pharmacyBenefitDetails.pharmacyBenefits.length > 0
  ) {
    return state.pharmacyBenefitDetails.pharmacyBenefits[0];
  }
});

export const patientQuery = {
  getPageStage,
  getError,
  getSearchRows,
  getResults,
  getResultsFiltered,
  getResultFilter,
  getResultFilterOptions,
  isMultipleMatch,
  isSingleNonExactMatch,
  getPatientRowDetailLoading,
  getPatientEstimateDetailsLoading,
  getPatientEstimateResultLoading,
  getPatientRowInactiveLoading,
  getPatientDetail,
  getPatientAsOfDate,
  getBenefitReportDetails,
  displayPatientDetails,
  getAlliances,
  getCcfBenefit,
  getCoverageDetail,
  getCostEstimationDetail,
  getServiceProcedures,
  estimateType,
  getCostEstimationResult,
  submitEstimateError,
  getSubscriberDetail,
  getEligibilityDate,
  getServiceContacts,
  getPlanLevelAccum,
  getPrimaryCareDetails,
  getAccumulatedServiceData,
  getBenefitTags,
  serviceAccumTagData,
  getPatientRouteData,
  getCobDetails,
  getPharmacyBenefitDetails,
  getPatientCompositeKey,
  getMaternityBenefit,
  getHealthWellness,
  getNotificationDetails,
  getAmInNetworkStatus,
  getProviderGroups,
  getProviders,
  getUnsortedProviderDetails,
  getProviderDetails,
  getInOutNetworkDetails,
  getProductDetails,
  getFrequencyDetails,
  dentalOfficeDetails,
  idCardDetails,
  getIdCardError,
  getVisionServices,
  getInfertilityServices,
  sureFitDetails,
  getPlanLevelTags,
  ohpTin,
  getPrecertUrl,
  isAccountRestricted,
  getSureFitUrl,
  getMedAmInNetworkSelectedTin,
  getPharmacyBenefit,
  getVisionOnlyDetails,
};
